import { receivableInvoicesApiNamespace } from 'receivable-invoices-js/constants/hosts';
import { qontoClient } from 'receivable-invoices-js/react/utils/api-clients/qonto';
import { fetchSigningDetails, fetchSigningDetailsKey } from './fetch-signing-details';

vi.mock('@repo/design-system-kit', () => ({
  useToast: vi.fn(() => ({
    showToast: vi.fn(),
  })),
}));

vi.mock('receivable-invoices-js/react/utils/api-clients/qonto');
vi.mock('receivable-invoices-js/constants/hosts', () => ({
  receivableInvoicesApiNamespace: '/api/v5',
}));

describe('unit | API | queries | fetch-signing-details', () => {
  const mockQontoClient = vi.mocked(qontoClient);
  const quoteId = 'quote-123';
  const clientId = 'client-456';
  const URL = `${receivableInvoicesApiNamespace}/receivable_invoices/quotes/shared`;

  beforeEach(() => {
    vi.clearAllMocks();
  });

  describe(fetchSigningDetailsKey, () => {
    it('generates correct query key', () => {
      const result = fetchSigningDetailsKey(quoteId, clientId);

      expect(result).toStrictEqual(['signing-details', quoteId, clientId]);
    });
  });

  describe(fetchSigningDetails, () => {
    it('fetches signing details successfully', async () => {
      const apiResponse = {
        electronic_signature_url: 'https://yousign.example.com/sign/123',
        status: 'pending_approval',
        error: null,
      };

      mockQontoClient.mockResolvedValue(apiResponse);

      const result = await fetchSigningDetails({ quoteId, clientId }, {});

      expect(mockQontoClient).toHaveBeenCalledWith(`${URL}/${quoteId}/signing/${clientId}`);
      expect(result).toStrictEqual({
        electronicSignatureUrl: 'https://yousign.example.com/sign/123',
        status: 'pending_approval',
        error: null,
      });
    });

    it('fetches signing details with error status', async () => {
      const apiResponse = {
        electronic_signature_url: null,
        status: 'failed',
        error: 'Signing failed',
      };

      mockQontoClient.mockResolvedValue(apiResponse);

      const result = await fetchSigningDetails({ quoteId, clientId }, {});

      expect(mockQontoClient).toHaveBeenCalledWith(`${URL}/${quoteId}/signing/${clientId}`);
      expect(result).toStrictEqual({
        electronicSignatureUrl: null,
        status: 'failed',
        error: 'Signing failed',
      });
    });

    it('fetches signing details with canceled status', async () => {
      const apiResponse = {
        electronic_signature_url: null,
        status: 'canceled',
        error: null,
      };

      mockQontoClient.mockResolvedValue(apiResponse);

      const result = await fetchSigningDetails({ quoteId, clientId }, {});

      expect(mockQontoClient).toHaveBeenCalledWith(`${URL}/${quoteId}/signing/${clientId}`);
      expect(result).toStrictEqual({
        electronicSignatureUrl: null,
        status: 'canceled',
        error: null,
      });
    });

    it('handles API errors with custom error handler', async () => {
      const error = new Error('API Error');
      mockQontoClient.mockRejectedValue(error);

      const onError = vi.fn<(err: unknown) => void>();
      await fetchSigningDetails({ quoteId, clientId }, { onError });

      expect(onError).toHaveBeenCalledWith(error);
    });

    it('throws error when no error handler is provided', async () => {
      const error = new Error('API Error');
      mockQontoClient.mockRejectedValue(error);

      await expect(fetchSigningDetails({ quoteId, clientId }, {})).rejects.toThrow('API Error');
    });

    it('handles empty response gracefully', async () => {
      const apiResponse = {};

      mockQontoClient.mockResolvedValue(apiResponse);

      const result = await fetchSigningDetails({ quoteId, clientId }, {});

      expect(result).toStrictEqual({});
    });

    it('uses correct URL with quote and client IDs', async () => {
      const differentQuoteId = 'quote-789';
      const differentClientId = 'client-012';
      const apiResponse = {
        electronic_signature_url: 'https://yousign.example.com/sign/789',
        status: 'pending_approval',
      };

      mockQontoClient.mockResolvedValue(apiResponse);

      await fetchSigningDetails({ quoteId: differentQuoteId, clientId: differentClientId }, {});

      expect(mockQontoClient).toHaveBeenCalledWith(
        `${URL}/${differentQuoteId}/signing/${differentClientId}`
      );
    });
  });
});
